import styled from "@emotion/styled";
import { DateTimeFormatter, LocalTime } from "@js-joda/core";
import React from "react";
import { Page } from "../../../activities/AppPage";
import {
  CommCenterMessageActionId,
  CommCenterTicketId,
  HRQuestionId,
  HRSectionId,
  HRSubSectionId,
} from "../../../messages";
import {
  CaregiverCommCenterMessage,
  CommCenterMessagePayload,
} from "../../../messages/CommunicationCenter";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { AppButton } from "../../Generic/AppButton";
import { ChatMessageTicksType, CommCenterListActionItem } from "../utils/chatUtils";
import ChatAvatar from "./ChatAvatar";
import ChatMessageBubble from "./ChatMessageBubble";
import ChatMessageContent from "./ChatMessageContent";
import ChatMessageTicks from "./ChatMessageTicks";
import { FileDocumentIcon, PencilSquareIcon } from "../../../utils/icones";

interface BaseProps {
  payload: CommCenterMessagePayload;
  ticketId: CommCenterTicketId;
  showActionsList: boolean;
  isLatestMessage: boolean;
  imagePreview: string | null;
  onSendMessage: (params: {
    ticketId: CommCenterTicketId;
    message: CaregiverCommCenterMessage;
  }) => void;
  onClickActionItem: (item: CommCenterListActionItem, actionId: CommCenterMessageActionId) => void;
  onRequestImagePreview: (image: string | null) => void;
  onRequestNavigate: (page: Page) => void;
}

type Props =
  | ({
      variant: "incoming";
      avatarUrl: string | null;
      senderName: string;
      time: LocalTime;
      showComplianceMessageActionButton: boolean;
      complianceItemActionItemDetails:
        | {
            type: "REJECTED_ITEM";
            sectionId: HRSectionId;
            subSectionId: HRSubSectionId;
            questionId: HRQuestionId;
          }
        | {
            type: "APPROVED_ITEM";
          }
        | null;
      onRequestNavigate: (page: Page) => void;
    } & BaseProps)
  | ({
      variant: "outgoing";
      time: LocalTime;
      ticks: ChatMessageTicksType;
    } & BaseProps);

const timeFormatter = DateTimeFormatter.ofPattern("HH:mm");

function ChatMessage(props: Props) {
  return (
    <ChatMessage.Root variant={props.variant}>
      {props.variant === "incoming" ? <ChatAvatar url={props.avatarUrl} /> : null}
      <ChatMessageBubble variant={props.variant}>
        <ChatMessageBubble.Header>
          {props.variant === "incoming" ? <span>{props.senderName}</span> : null}
          <ChatMessageBubble.HeaderChild>
            <ChatMessageBubble.Time>{props.time.format(timeFormatter)}</ChatMessageBubble.Time>
            {props.variant === "outgoing" ? <ChatMessageTicks ticks={props.ticks} /> : null}
          </ChatMessageBubble.HeaderChild>
        </ChatMessageBubble.Header>
        <ChatMessageContent
          ticketId={props.ticketId}
          payload={props.payload}
          showActionsList={props.showActionsList}
          isLatestMessage={props.isLatestMessage}
          imagePreview={props.imagePreview}
          onClickActionItem={props.onClickActionItem}
          onRequestImagePreview={props.onRequestImagePreview}
        />
        {props.variant === "incoming" &&
        props.showComplianceMessageActionButton &&
        props.complianceItemActionItemDetails !== null ? (
          <ComplianceItemStateMessageAction
            item={props.complianceItemActionItemDetails}
            onRequestNavigate={props.onRequestNavigate}
          />
        ) : (
          <></>
        )}
      </ChatMessageBubble>
    </ChatMessage.Root>
  );
}

ChatMessage.Root = styled.div<{ variant: "incoming" | "outgoing" }>`
  display: flex;
  flex-direction: row;
  justify-content: ${({ variant }) => (variant === "incoming" ? "flex-start" : "flex-end")};
  gap: 4px;
  width: 100%;
  white-space: pre-line;
`;

export default ChatMessage;

const ComplianceItemStateMessageAction = (props: {
  item:
    | {
        type: "REJECTED_ITEM";
        sectionId: HRSectionId;
        subSectionId: HRSubSectionId;
        questionId: HRQuestionId;
      }
    | {
        type: "APPROVED_ITEM";
      };
  onRequestNavigate: (page: Page) => void;
}) => {
  const text =
    props.item.type === "APPROVED_ITEM"
      ? translate(STRINGS.COMPLETE_THE_APPLICATION_ONBOARDING_MESSAGE_BUTTON_TEXT)
      : translate(STRINGS.FIX_IT_NOW_ONBOARDING_MESSAGE_BUTTON_TEXT);

  const pageNavigationParams:
    | {
        type: "OnboardingActivity";
      }
    | {
        type: "OnboardingActivityWithDirection";
        customDirectionParams: {
          type: "Question";
          questionId: HRQuestionId;
          sectionId: HRSectionId;
          subsectionId: HRSubSectionId;
        };
      } =
    props.item.type === "APPROVED_ITEM"
      ? { type: "OnboardingActivity" }
      : {
          type: "OnboardingActivityWithDirection",
          customDirectionParams: {
            type: "Question",
            questionId: props.item.questionId,
            sectionId: props.item.sectionId,
            subsectionId: props.item.subSectionId,
          },
        };

  return (
    <ComplianceItemStateMessageAction.ActionButton
      size="extra-small"
      onClick={() => props.onRequestNavigate(pageNavigationParams)}
    >
      {props.item.type === "REJECTED_ITEM" ? <PencilSquareIcon /> : <FileDocumentIcon />}
      {text}
    </ComplianceItemStateMessageAction.ActionButton>
  );
};

ComplianceItemStateMessageAction.ActionButton = styled(AppButton)`
  font-size: 12px;
  margin-bottom: 5px;
  margin-top: 10px;
  align-items: center;
  border-radius: 14px;
  display: flex;
  flex-direction: row;
  gap: 10px;
`;
